import type { ReactNode, JSX } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { getPreviousPeriodDateRange } from '@buffer-mono/date-utils'
import { useSplitEnabled } from '@buffer-mono/features'
import {
  Flex,
  Link as PopcornLink,
  PageHeader,
  PageLayout,
  Separator,
  Text,
} from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { useShouldShowDeveloperData } from '~publish/components/DeveloperData'
import {
  FilterByChannel,
  useFilterByChannelFragment,
} from '~publish/components/FilterByChannel'
import { FilterByTag } from '~publish/components/FilterByTag'
import { SplitTagBadge } from '~publish/components/SplitTagBadge'
import {
  useIsFreeUser,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { buildAnalyzeHomeUrl } from '~publish/helpers/externalUrls'
import { useAnalyzeSunsetEnabled } from '~publish/hooks/useAnalyzeSunsetEnabled'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { canPerform } from '~publish/utils/permissions/canPerform'
import { selectEnabledChannelIds } from '~publish/utils/permissions/effectiveChannelSelection'
import { areAllChannelsViewOnlyForPublish } from '~publish/utils/permissions/viewOnlyPublish'
import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { ViewOnlyPublishCoachMark } from '~publish/components/ViewOnlyCoachMarks'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { PostingTimePreviewModal } from '~publish/pages/Channel/ChannelSettings/PostingSchedule/components/PostingTimePreviewModal'
import { countActiveSlots } from '~publish/pages/Channel/ChannelSettings/PostingSchedule/utils/countActiveSlots'

import { AllChannelsInsightsNotSupported } from './AllChannelsInsightsNotSupported'
import { useAllChannelsInsightsSummary } from './hooks/useAllChannelsInsightsSummary'
import { useAllChannelsTimeSeries } from './hooks/useAllChannelsTimeSeries'
import { useCrossChannelsTakeaways } from './hooks/useCrossChannelsTakeaways'
import { useRefreshCrossChannelsTakeaways } from './hooks/useRefreshCrossChannelsTakeaways'
import type { TopPostsSortBy } from '../graphqlEnums'
import {
  getAllChannelsInsightsContentState,
  getInsightsChannelTypeLabel,
  isChannelSupportedByInsights,
} from '../utils'
import { getViewOnlyInsightsCopy } from '../viewOnlyCopy'
import {
  AllChannelsFollowersChart,
  AllChannelsPerformanceTable,
  AllChannelsPostsChart,
  AllChannelsTopPosts,
  MAX_CHART_CHANNELS,
} from './sections'
import {
  DateRangeToggle,
  InsightsAccessDenied,
  InsightsActionsMenu,
  InsightsDeveloperActionsMenu,
  InsightsFilterDropdown,
  InsightsNavigationTabs,
  InsightsToolbar,
  SummaryMetrics,
  Takeaways,
} from '../components'
import { ExportConfigDialog } from '../components/ExportConfigDialog'
import type { DeveloperDataProps } from '../components/InsightsActionsMenu'
import { ExportDropdown } from '../components/ExportDropdown'
import { InsightsPageTitle } from '../components/InsightsPageTitle'
import { InsightsUpgradeDialog } from '../components/InsightsUpgradeDialog'
import { AllChannelsSyncIcon } from '../components/SyncIcon'
import {
  InsightsPageProvider,
  InsightsSnapshotProvider,
  useInsightsChannels,
  useInsightsDateRange,
  useInsightsFilterInput,
  useInsightsTags,
  useInsightsUpgradePath,
} from '../context'
import {
  useColumnVisibility,
  useDownloadSnapshots,
  useInsightsExportAction,
  useTakeawaysGate,
  useTopPosts,
} from '../hooks'
import { getTakeawayScheduleTarget } from '../InsightsChannel/getTakeawayScheduleTarget'
import styles from './AllChannelsInsightsPage.module.css'

// ========================================
// Entry point — thin wrapper that mounts the provider
// ========================================

export function AllChannelsInsightsPage(): JSX.Element {
  return (
    <InsightsPageProvider>
      <InsightsSnapshotProvider>
        <AllChannelsInsightsPageContent />
      </InsightsSnapshotProvider>
    </InsightsPageProvider>
  )
}

// ========================================
// Page content — consumes the provider
// ========================================

const CROSS_CHANNELS_TAKEAWAYS_SPLIT = 'cross-channels-takeaways'

function AllChannelsInsightsPageContent(): JSX.Element {
  const { search } = useLocation()
  const [topPostsSortBy, setTopPostsSortBy] =
    useState<TopPostsSortBy>('reactions')

  // Active sort of the performance table, lifted here so the export carries it
  // (CORE-6039). Owns the toggle logic; the table is a controlled consumer.
  // `key` is a `PostMetricType` value; default mirrors the table's default.
  const [channelSort, setChannelSort] = useState<{
    key: string
    direction: 'asc' | 'desc'
  }>({ key: 'postCount', direction: 'desc' })

  const handleChannelSort = useCallback((metricType: string): void => {
    setChannelSort((prev) =>
      prev.key === metricType
        ? {
            key: metricType,
            direction: prev.direction === 'desc' ? 'asc' : 'desc',
          }
        : { key: metricType, direction: 'desc' },
    )
  }, [])

  const organizationId = useOrganizationId() ?? ''
  const isFreeUser = useIsFreeUser()
  const isAnalyzeSunset = useAnalyzeSunsetEnabled()
  // Free users never had the link; once Analyze is retired nobody does.
  const shouldShowAnalyzeLink = !isFreeUser && !isAnalyzeSunset
  const weekStartsOn: 0 | 1 = useWeekStartsOn() === 'monday' ? 1 : 0

  const {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange,
    onCustomRangeApply,
  } = useInsightsDateRange()
  const { selectedTagIds, tagFilter, setSelectedTagIds } = useInsightsTags()
  const {
    shouldDisplayUpgradePath,
    isCustomDateUpgradeOpen,
    setIsCustomDateUpgradeOpen,
    handleCustomBlocked,
    handleCustomDateUpgradeClick,
  } = useInsightsUpgradePath()
  // All-channels-specific: channel filter via URL query param
  const [channelQueryParam = [], setChannelFilter] =
    useQueryParam<string[]>('channels')

  const { channels } = useInsightsChannels()

  // A channel is usable in Insights only when both gates pass:
  //  1. the actor can `viewInsights` on it (per-channel permission), and
  //  2. its type is supported by Insights at all.
  // The second gate matters because publish-capable types that Insights has no
  // data for — Facebook Groups, Instagram personal profiles — still pass
  // `viewInsights`, so without this check they'd appear selectable here yet hit
  // the "Insights aren't available" screen. Both gates grey the channel out in
  // the filter and keep it out of data/export requests.
  const viewableChannelIds = useMemo(
    () =>
      channels
        .filter(
          (channel) =>
            canPerform(channel, 'viewInsights') &&
            isChannelSupportedByInsights(channel.service, channel.type),
        )
        .map((channel) => channel.id),
    [channels],
  )
  const disabledChannelIds = useMemo(
    () =>
      new Set(
        channels
          .filter(
            (channel) =>
              !canPerform(channel, 'viewInsights') ||
              !isChannelSupportedByInsights(channel.service, channel.type),
          )
          .map((channel) => channel.id),
      ),
    [channels],
  )

  // Explains, per disabled row, why a channel can't be selected — surfaced as a
  // tooltip in the filter. Mirrors the two gates above: an unsupported type
  // gets the same "Insights aren't available for {type}" wording as the
  // per-channel unsupported screen; a supported-but-unauthorized channel gets a
  // permission message.
  const disabledChannelReasons = useMemo(() => {
    const reasons = new Map<string, ReactNode>()
    for (const channel of channels) {
      if (!isChannelSupportedByInsights(channel.service, channel.type)) {
        reasons.set(
          channel.id,
          `Insights aren't available for ${getInsightsChannelTypeLabel(
            channel.service,
            channel.type,
          )}.`,
        )
      } else if (!canPerform(channel, 'viewInsights')) {
        reasons.set(
          channel.id,
          "You don't have access to this channel's insights.",
        )
      }
    }
    return reasons
  }, [channels])
  const getDisabledChannelReason = useCallback(
    (channelId: string): ReactNode => disabledChannelReasons.get(channelId),
    [disabledChannelReasons],
  )

  // The channels[] URL param is user-editable (bookmarks, hand-typed URLs), so
  // the selection this page acts on — filter dropdowns and data/export
  // requests — is its intersection with the viewable channels; a forced id for
  // a disabled channel must not render as selected.
  const effectiveChannelSelection = useMemo(
    () =>
      selectEnabledChannelIds(channelQueryParam, channels, disabledChannelIds),
    [channelQueryParam, channels, disabledChannelIds],
  )

  const hasChannelFilter = channelQueryParam.length > 0
  // Channels we actually request data/exports for: the effective selection,
  // or every viewable channel when unfiltered. Never send a channel the actor
  // can't `viewInsights`.
  const requestChannelIds = useMemo(
    () => (hasChannelFilter ? effectiveChannelSelection : viewableChannelIds),
    [hasChannelFilter, effectiveChannelSelection, viewableChannelIds],
  )

  // The metrics chart draws at most MAX_CHART_CHANNELS lines, so fetch no more.
  const chartChannelIds = useMemo(
    () => requestChannelIds.slice(0, MAX_CHART_CHANNELS),
    [requestChannelIds],
  )

  const { isEnabled, isReady } = useSplitEnabled(CROSS_CHANNELS_TAKEAWAYS_SPLIT)
  const isCrossChannelTakeawaysEnabled = isReady && isEnabled
  // Takeaway cards badge themselves from the channels they concern, which needs
  // the names and avatars the page's channels carry behind the filter fragment.
  const unmaskedChannels = useFilterByChannelFragment(channels)
  const postableChannelIds = useMemo(
    () =>
      new Set(
        channels
          .filter((channel) => canPerform(channel, 'manageUpdates'))
          .map((channel) => channel.id),
      ),
    [channels],
  )
  // A card's composer CTA acts on the first channel the takeaway names, so each
  // channel carries its own answer rather than the section carrying one for all.
  const takeawayChannels = useMemo(
    () =>
      unmaskedChannels.map((channel) => ({
        id: channel.id,
        name: channel.name,
        avatar: channel.avatar,
        service: channel.service,
        canCreatePosts: postableChannelIds.has(channel.id),
      })),
    [unmaskedChannels, postableChannelIds],
  )
  const [recommendedTimesChannelId, setRecommendedTimesChannelId] = useState<
    string | null
  >(null)
  const recommendedTimesChannel = recommendedTimesChannelId
    ? channels.find((channel) => channel.id === recommendedTimesChannelId)
    : undefined
  const recommendedTimesChannelDetails = recommendedTimesChannelId
    ? unmaskedChannels.find(
        (channel) => channel.id === recommendedTimesChannelId,
      )
    : undefined
  const recommendedTimesChannelIds = useMemo(
    () => (recommendedTimesChannelId ? [recommendedTimesChannelId] : []),
    [recommendedTimesChannelId],
  )
  const {
    counts: recommendedTimesPostCounts,
    loading: recommendedTimesPostCountsLoading,
  } = usePostCounts({
    organizationId: recommendedTimesChannelId ? organizationId : undefined,
    channelIds: recommendedTimesChannelIds,
  })
  const handleOpenRecommendedTimesDialog = useCallback(
    (channelId: string): void => setRecommendedTimesChannelId(channelId),
    [],
  )
  const handleRecommendedTimesOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) setRecommendedTimesChannelId(null)
    },
    [],
  )
  const recommendedTimesPostsPerWeek = getTakeawayScheduleTarget({
    activeSlotCount: countActiveSlots(recommendedTimesChannel?.postingSchedule),
    postingGoal: recommendedTimesChannel?.postingGoal?.goal,
  })
  // Omitting `channelIds` asks the question of the whole organization: the
  // get-started panel is about whether the account posts at all, so no filter
  // the page applies may narrow it.
  const takeawaysGate = useTakeawaysGate({
    // Withhold the org id so the count skips while the section is hidden.
    organizationId: isCrossChannelTakeawaysEnabled ? organizationId : undefined,
  })

  const { data: crossChannelTakeaways, loading: takeawaysLoading } =
    useCrossChannelsTakeaways({
      organizationId,
      channelIds: requestChannelIds,
      skip:
        !isCrossChannelTakeawaysEnabled ||
        !takeawaysGate.shouldRequestTakeaways,
    })
  const { refresh: refreshTakeaways, loading: takeawaysRefreshing } =
    useRefreshCrossChannelsTakeaways({
      organizationId,
      channelIds: requestChannelIds,
    })
  const shouldShowDeveloperData = useShouldShowDeveloperData()
  const developerData: DeveloperDataProps | undefined =
    shouldShowDeveloperData &&
    isCrossChannelTakeawaysEnabled &&
    requestChannelIds.length > 0
      ? {
          kind: 'allChannels',
          takeawaysLoading,
          takeawaysRefreshing,
          onRefreshTakeaways: refreshTakeaways,
        }
      : undefined

  // Either the sections, or the state that stands in when nothing is
  // requestable — no insights access at all, a URL filter that selected only
  // disabled channels, or channel types Insights doesn't cover.
  const contentState = useMemo(
    () =>
      getAllChannelsInsightsContentState({
        channels,
        requestChannelCount: requestChannelIds.length,
      }),
    [channels, requestChannelIds],
  )
  // Both stand-in states replace the sections, so their queries would discard
  // whatever they returned.
  const skipInsightsData = contentState !== 'insights'

  const onClearChannelFilter = hasChannelFilter
    ? (): void => setChannelFilter([])
    : undefined

  // Summary cards + performance table. This query omits the slow time-series
  // field, so it returns as soon as the (eager) summary use case resolves,
  // without waiting on the metrics graph.
  const {
    summary,
    channelInsights,
    loading: summaryLoading,
    error: summaryError,
  } = useAllChannelsInsightsSummary({
    organizationId,
    startDate,
    endDate,
    channelIds: requestChannelIds,
    tags: tagFilter,
    skip: skipInsightsData,
  })

  // Metrics graph. Decoupled onto its own query so it has an independent
  // loading/refetch lifecycle and never blocks the summary + table above.
  const {
    channelTimeSeries,
    loading: timeSeriesLoading,
    error: timeSeriesError,
  } = useAllChannelsTimeSeries({
    organizationId,
    startDate,
    endDate,
    channelIds: chartChannelIds,
    tags: tagFilter,
    skip: skipInsightsData,
  })

  // Build filter input for top-posts query using the channel filter override
  const insightsFilter = useInsightsFilterInput({
    channelIds: requestChannelIds,
  })

  const {
    topPosts,
    totalCount: topPostsTotalCount,
    loading: topPostsLoading,
    error: topPostsError,
  } = useTopPosts({
    filter: insightsFilter,
    sort: { field: topPostsSortBy, direction: 'desc' },
    first: 5,
    skip: skipInsightsData,
  })

  // Each section tracks its own query's state: show a skeleton only on initial
  // load (no data yet), and a subtler "refreshing" state once data exists.
  const summaryHasData = channelInsights.length > 0
  const summaryInitialLoading = summaryLoading && !summaryHasData
  const summaryRefreshing = summaryLoading && summaryHasData

  const chartHasData = channelTimeSeries.length > 0
  const chartInitialLoading = timeSeriesLoading && !chartHasData
  const chartRefreshing = timeSeriesLoading && chartHasData

  const comparisonRange = useMemo(() => {
    const { previousStartDate, previousEndDate } = getPreviousPeriodDateRange(
      startDate,
      endDate,
    )
    return { startDate: previousStartDate, endDate: previousEndDate }
  }, [startDate, endDate])

  // Read the performance table's column selection so the export reflects it.
  // This is a second `useColumnVisibility` instance for the same scope as the
  // table's — popcorn's useLocalStorage broadcasts a `local-storage` event on
  // write, so both instances stay in sync within the tab (no need to lift the
  // table's state). availableMetrics must match the table's input for the
  // preference to resolve identically.
  const performanceMetrics = useMemo(
    () => channelInsights.flatMap((insight) => insight.metrics),
    [channelInsights],
  )
  const { visibleMetrics: performanceColumns } = useColumnVisibility({
    availableMetrics: performanceMetrics,
    scopeId: 'all-channels',
  })

  const exportableChannelIds = useMemo(() => {
    const exportable = new Set(
      channels
        .filter((channel) => canPerform(channel, 'exportInsights'))
        .map((channel) => channel.id),
    )
    return requestChannelIds.filter((id) => exportable.has(id))
  }, [channels, requestChannelIds])

  const canExport = exportableChannelIds.length > 0
  const isViewOnly = areAllChannelsViewOnlyForPublish(channels)
  // PDF export configuration dialog (sections + logo).
  const [isExportConfigOpen, setIsExportConfigOpen] = useState(false)
  const {
    showExport,
    showUpgradePath: showExportUpgradePath,
    isExporting,
    isLongRunning,
    handleSelect: handleExportSelect,
    exportWithConfig,
    isUpgradeDialogOpen: isExportUpgradeDialogOpen,
    onUpgradeDialogOpenChange: onExportUpgradeDialogOpenChange,
    handleUpgradeClick: handleExportUpgradeClick,
  } = useInsightsExportAction(
    {
      organizationId: organizationId ?? '',
      channelIds: exportableChannelIds,
      canExport,
      startDate: insightsFilter.startDate,
      endDate: insightsFilter.endDate,
      tags: tagFilter,
      postCount: topPostsTotalCount,
      // Carry the performance table's active sort so the export orders channels
      // (and picks which survive the PDF channel cap) the way the user sees them.
      channelSort: {
        metric: channelSort.key,
        direction: channelSort.direction,
      },
      // Carry the Top Posts sort too, so the exported posts (CSV/Markdown post
      // lists) match the on-screen Top Posts order. The Top Posts query is
      // hardcoded to descending, so mirror that here.
      postSort: { field: topPostsSortBy, direction: 'desc' },
      // Carry the performance table's visible columns (in display order) so the
      // exported channel summary table shows the same metrics.
      channelColumns: performanceColumns.map((metric) => metric.type),
    },
    { onConfigurePdf: () => setIsExportConfigOpen(true) },
  )

  // Separate from handleExportSelect: the snapshot download bypasses the export
  // upgrade dialog (it's not paywalled).
  const {
    downloadSnapshots,
    isPreparing: isPreparingSnapshots,
    canDownload: canDownloadSnapshots,
  } = useDownloadSnapshots()

  return (
    <>
      <Outlet />
      <PageLayout>
        <PageHeader separator={false}>
          <PageHeader.Row>
            <InsightsPageTitle />
            <PageHeader.Actions>
              <Flex direction="row" align="center" gap="xs">
                {/* Wide layout — hidden when the page-header container < 650px */}
                <div className={styles.expandedActions}>
                  <PageHeader.ActionGroup className={styles.primaryActions}>
                    {shouldShowAnalyzeLink && (
                      <>
                        <div
                          className={styles.analyzeCtaFull}
                          data-testid="insights-analyze-cta-full"
                        >
                          <Text color="subtle" size="sm">
                            Looking for more metrics and reports?{' '}
                            <PopcornLink href={buildAnalyzeHomeUrl()} external>
                              Go to Analyze
                            </PopcornLink>
                          </Text>
                        </div>
                        <div
                          className={styles.analyzeCtaCompact}
                          data-testid="insights-analyze-cta-compact"
                        >
                          <Text size="sm">
                            <PopcornLink href={buildAnalyzeHomeUrl()} external>
                              Go to Analyze
                            </PopcornLink>
                          </Text>
                        </div>
                        {/* Divides the Analyze link from the controls beside
                            it. The only divider the header keeps. */}
                        <Separator
                          orientation="vertical"
                          decorative
                          className={styles.separator}
                        />
                      </>
                    )}
                    {isViewOnly && (
                      <div className={styles.viewOnlyBadge}>
                        <ViewOnlyPublishCoachMark
                          copy={getViewOnlyInsightsCopy({
                            surface: 'allChannels',
                            canExport,
                          })}
                        >
                          <ViewOnlyBadge />
                        </ViewOnlyPublishCoachMark>
                      </div>
                    )}
                    {/* Feedback sits just left of Sync, in the action cluster. */}
                    <div className={styles.feedbackAction}>
                      <FeedbackWidget
                        id="all-channels-insights-1"
                        source="insights"
                      />
                    </div>
                    <AllChannelsSyncIcon />
                    {developerData && (
                      <InsightsDeveloperActionsMenu
                        developerData={developerData}
                      />
                    )}
                  </PageHeader.ActionGroup>
                  {/* Export is its own group, so it sits a step further from
                      the cluster. Guarded because ExportDropdown renders
                      nothing without `showExport`, and an empty group would
                      still hold the wider gap open. */}
                  {showExport && (
                    <PageHeader.ActionGroup>
                      <ExportDropdown
                        showExport={showExport}
                        showUpgradePath={showExportUpgradePath}
                        isExporting={isExporting}
                        isLongRunning={isLongRunning}
                        onSelect={handleExportSelect}
                        onDownloadSnapshots={downloadSnapshots}
                        canDownloadSnapshots={canDownloadSnapshots}
                        isPreparingSnapshots={isPreparingSnapshots}
                      />
                    </PageHeader.ActionGroup>
                  )}
                </div>

                {/* Narrow layout — ellipsis menu shown when the container < 650px */}
                <div className={styles.collapsedActionsMenu}>
                  <InsightsActionsMenu
                    analyzeUrl={
                      shouldShowAnalyzeLink ? buildAnalyzeHomeUrl() : undefined
                    }
                    showExport={showExport}
                    showUpgradePath={showExportUpgradePath}
                    isExporting={isExporting}
                    isLongRunning={isLongRunning}
                    onExportSelect={handleExportSelect}
                    onDownloadSnapshots={downloadSnapshots}
                    canDownloadSnapshots={canDownloadSnapshots}
                    isPreparingSnapshots={isPreparingSnapshots}
                    developerData={developerData}
                  />
                </div>
              </Flex>
            </PageHeader.Actions>
          </PageHeader.Row>
          <InsightsNavigationTabs
            activeTab="post-insights"
            postInsightsHref={`/insights${search}`}
          />
        </PageHeader>

        <PageLayout.Container>
          <PageHeader.Row className={styles.toolbarRow}>
            <InsightsToolbar>
              <DateRangeToggle
                selectedDateRange={selectedDateRange}
                onDateRangeChange={onDateRangeChange}
                currentRange={{ startDate, endDate }}
                onCustomRangeApply={onCustomRangeApply}
                weekStartsOn={weekStartsOn}
                showUpgradePath={shouldDisplayUpgradePath}
                onCustomBlocked={handleCustomBlocked}
              />
              <InsightsToolbar.Filters>
                {/* Inline filters — shown while the header is wide enough */}
                <div className={styles.inlineFilters}>
                  <FilterByChannel
                    channels={channels}
                    value={effectiveChannelSelection}
                    onSelect={setChannelFilter}
                    disabledChannelIds={disabledChannelIds}
                    getDisabledChannelReason={getDisabledChannelReason}
                  />
                  <FilterByTag
                    value={selectedTagIds}
                    onSelect={setSelectedTagIds}
                  />
                </div>
                {/* Collapsed into a ListFilterIcon dropdown when the header tightens */}
                <InsightsFilterDropdown
                  className={styles.filterDropdown}
                  channels={channels}
                  selectedChannels={effectiveChannelSelection}
                  onChannelSelect={setChannelFilter}
                  selectedTags={selectedTagIds}
                  onTagSelect={setSelectedTagIds}
                  disabledChannelIds={disabledChannelIds}
                />
              </InsightsToolbar.Filters>
            </InsightsToolbar>
          </PageHeader.Row>
        </PageLayout.Container>

        <PageLayout.Container className={styles.contentContainer}>
          {contentState === 'notSupported' ? (
            <AllChannelsInsightsNotSupported channels={channels} />
          ) : contentState === 'accessDenied' ? (
            <InsightsAccessDenied />
          ) : (
            <Flex direction="column" gap="lg">
              {isCrossChannelTakeawaysEnabled && (
                <Takeaways
                  gate={takeawaysGate}
                  variant="cross-channel"
                  channels={takeawayChannels}
                  channelService={null}
                  canCreatePosts={postableChannelIds.size > 0}
                  onOpenRecommendedTimesDialog={
                    handleOpenRecommendedTimesDialog
                  }
                  badge={
                    <SplitTagBadge splitName={CROSS_CHANNELS_TAKEAWAYS_SPLIT} />
                  }
                  takeaways={crossChannelTakeaways}
                  loading={
                    takeawaysLoading && crossChannelTakeaways.length === 0
                  }
                  refreshing={
                    (takeawaysLoading && crossChannelTakeaways.length > 0) ||
                    takeawaysRefreshing
                  }
                />
              )}
              {isCrossChannelTakeawaysEnabled &&
                recommendedTimesChannel &&
                recommendedTimesChannelDetails &&
                !recommendedTimesPostCountsLoading && (
                  <PostingTimePreviewModal
                    open
                    onOpenChange={handleRecommendedTimesOpenChange}
                    channel={{
                      id: recommendedTimesChannel.id,
                      service: recommendedTimesChannelDetails.service,
                      avatar: recommendedTimesChannelDetails.avatar,
                      name: recommendedTimesChannelDetails.name,
                    }}
                    strategy={{
                      optimize: {
                        updateMode: 'add',
                        postsPerWeek: recommendedTimesPostsPerWeek,
                      },
                    }}
                    hasQueuedPosts={
                      (recommendedTimesPostCounts?.queue ?? 0) > 0
                    }
                  />
                )}
              <SummaryMetrics
                metrics={summary}
                loading={summaryInitialLoading}
                refreshing={summaryRefreshing}
                error={summaryError}
                comparisonRange={comparisonRange}
                startDate={startDate}
                endDate={endDate}
                skeletonCount={4}
                selectedDateRange={selectedDateRange}
                hasChannelFilter={hasChannelFilter}
                onClearChannelFilter={onClearChannelFilter}
                shareContext={{
                  kind: 'allChannels',
                  channelIds: requestChannelIds,
                  totalChannelCount: channels.length,
                }}
                canCreatePosts={postableChannelIds.size > 0}
              />
              <AllChannelsTopPosts
                topPosts={topPosts}
                loading={topPostsLoading && topPosts.length === 0}
                refreshing={topPostsLoading && topPosts.length > 0}
                error={topPostsError}
                startDate={startDate}
                endDate={endDate}
                sortBy={topPostsSortBy}
                onSortByChange={setTopPostsSortBy}
                selectedDateRange={selectedDateRange}
                hasChannelFilter={hasChannelFilter}
                onClearChannelFilter={onClearChannelFilter}
              />
              <AllChannelsPerformanceTable
                channelInsights={channelInsights}
                loading={summaryInitialLoading}
                refreshing={summaryRefreshing}
                error={summaryError}
                startDate={startDate}
                endDate={endDate}
                comparisonStartDate={comparisonRange.startDate}
                comparisonEndDate={comparisonRange.endDate}
                selectedDateRange={selectedDateRange}
                hasChannelFilter={hasChannelFilter}
                onClearChannelFilter={onClearChannelFilter}
                sortConfig={channelSort}
                onSort={handleChannelSort}
              />
              <AllChannelsFollowersChart
                channelTimeSeries={channelTimeSeries}
                canCreatePosts={postableChannelIds.size > 0}
                totalChannelCount={viewableChannelIds.length}
                loading={chartInitialLoading}
                refreshing={chartRefreshing}
                error={timeSeriesError}
                startDate={startDate}
                endDate={endDate}
                selectedDateRange={selectedDateRange}
                hasChannelFilter={hasChannelFilter}
                onClearChannelFilter={onClearChannelFilter}
              />
              <AllChannelsPostsChart
                channelTimeSeries={channelTimeSeries}
                canCreatePosts={postableChannelIds.size > 0}
                totalChannelCount={viewableChannelIds.length}
                loading={chartInitialLoading}
                refreshing={chartRefreshing}
                error={timeSeriesError}
                startDate={startDate}
                endDate={endDate}
                selectedDateRange={selectedDateRange}
                hasChannelFilter={hasChannelFilter}
                onClearChannelFilter={onClearChannelFilter}
              />
            </Flex>
          )}
        </PageLayout.Container>
      </PageLayout>
      <InsightsUpgradeDialog
        open={isCustomDateUpgradeOpen}
        onOpenChange={setIsCustomDateUpgradeOpen}
        onUpgradeClick={handleCustomDateUpgradeClick}
      />
      {showExportUpgradePath && (
        <InsightsUpgradeDialog
          flow="export"
          open={isExportUpgradeDialogOpen}
          onOpenChange={onExportUpgradeDialogOpenChange}
          onUpgradeClick={handleExportUpgradeClick}
        />
      )}
      <ExportConfigDialog
        open={isExportConfigOpen}
        onOpenChange={setIsExportConfigOpen}
        variant="allChannels"
        onConfirm={exportWithConfig}
      />
    </>
  )
}
